import {Component, OnInit, OnDestroy} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import * as _ from 'lodash';
import {CustomerPondService} from '../../../../../services/customerPond.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {BusinessEntityService} from 'src/app/services/businessEntity.service';
import {ReadingsService} from '../../../../../services/readings.service';
import ReadingsModel from '../../../../../services/readings.model';
import BusinessEntity from 'src/app/services/businessEntity.model';
import CustomerDeviceModel from 'src/app/services/customerDevice.model';
import {BusinessTypeService} from 'src/app/services/businessType.service';
import BusinessTypeModel from 'src/app/services/businessType.model';
import {ActivatedRoute} from '@angular/router';
import {ExcelService} from 'src/app/services/excelService';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

/**
 * @component ProviderSummaryReportComponent
 * @selector app-provider-summary-report
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `ProviderSummaryReportComponent` generates a summary report specifically for Feed Providers or related business entities.
 * It allows users to view a consolidated list of customer ponds and devices associated with a specific provider.
 *
 * ## Key Responsibilities:
 * 1. **Business Selection**: Allows users to search and select a specific business entity (Feed Provider) to view reports for.
 *    - Filters available businesses based on specific Business Type IDs (likely Feed Provider categories).
 * 2. **Data Aggregation**: Fetches summary data which includes customer ponds and their associated devices.
 * 3. **Data Transformation**: Flattens the hierarchical structure of Ponds -> Devices into a linear list for display.
 * 4. **Categorization**: Separates data into:
 *    - `providerSummaryList`: Devices/Ponds associated with the provider but potentially owned by others.
 *    - `loginProviderSummaryList`: Devices/Ponds directly owned by the logged-in provider (if applicable).
 * 5. **Filtering**: Provides client-side search functionality to filter the displayed list by Business Name or Device Number.
 * 6. **Export**: Enables exporting the summary report to Excel.
 *
 * ## User Roles & Permissions:
 * - **Provider Business Admin**:
 *   - Automatically initialized with their own business context.
 *   - Can view their own summary immediately.
 * - **Other Users**:
 *   - Must search for and select a business from the dropdown.
 *   - The dropdown is filtered to show only specific business types (IDs: '618a17b80f339c001e991b03', '632aeccb0d7d43001d8813c2').
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: Used to determine if the user is a 'Business Admin'.
 * - `loginBusinessName`: Used to pre-fill or display the business name.
 * - `loginBusinessCategory`: Used to check if the user is a 'Provider'.
 * - `businessID`: Used to automatically set the `feedProviderId` for Provider Admins.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` fetches all businesses and filters them. If the user is a Provider Admin, it auto-selects their business.
 * 2. **Selection**: User selects a business from the autocomplete dropdown.
 * 3. **Fetching**: `providerSummary()` calls `CustomerPondService` to get the raw data.
 * 4. **Processing**: The raw data (Ponds) is iterated to extract Devices. Devices are enriched with Pond and Business info and added to the list.
 * 5. **Display**: The processed list is displayed in a table with pagination.
 */
@Component({
  selector: 'app-provider-summary-report',
  templateUrl: './provider-summary-report.component.html',
  styleUrls: ['./provider-summary-report.component.scss']
})
export class ProviderSummaryReportComponent implements OnInit, OnDestroy {
  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {BusinessEntity} The currently selected business entity. */
  public businessEntity: BusinessEntity;

  /** @type {Array<CustomerPondModel>} List of customer ponds fetched from the service. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {CustomerPondModel} Model used for binding selection criteria (feedProviderId). */
  public customerPond: CustomerPondModel;

  /** @type {ReadingsModel} Model for readings (used in export context). */
  public reading: ReadingsModel;

  /** @type {Array<BusinessTypeModel>} List of business types (unused in current logic). */
  public businessTypes: Array<BusinessTypeModel>;

  /** @type {BusinessTypeModel} Single business type model (unused in current logic). */
  public btype: BusinessTypeModel;

  /** @type {Array<CustomerDeviceModel>} The main list of devices/ponds displayed in the report. */
  public providerSummaryList: Array<CustomerDeviceModel>;

  /** @type {Array<CustomerDeviceModel>} Master copy of the provider summary list for filtering purposes. */
  public dupProviderSummaryList: Array<CustomerDeviceModel>;

  /** @type {Array<CustomerDeviceModel>} List of devices belonging specifically to the logged-in provider. */
  public loginProviderSummaryList: Array<CustomerDeviceModel>;

  /** @type {boolean} Flag for page loading state. Default is `false`. */
  public pgLoad = false;

  /** @type {string} The role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} The name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} The category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {string} Generic holder for business name. */
  public loginBusinessName: string;

  /** @type {boolean} Flag controlling the loading spinner for API requests. */
  public responseLoader: boolean;

  /** @type {boolean} Flag indicating if the search/submit button has been clicked. */
  public isSearchClicked: boolean;

  // pagination sorting
  /** @type {number} Current page number for pagination. Default is `1`. */
  public currentPageNumber: number = 1;

  /** @type {number} Number of items per page. Default is `15`. */
  public noOfItemsPerPage: number = 15;

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /** @type {boolean} Flag to display a "No Data" message. Default is `false`. */
  public NoDataMessage: boolean = false;

  /** @type {number} The current active page number. */
  public activePageNo: number;

  /** @type {string} Keyword for search operations. Default is 'businessName'. */
  public keyword: string;

  /** @type {any} Generic item holder. */
  public item: any;

  /** @type {string} The current search string entered by the user. */
  public search: string;

  /** @type {boolean} Flag to trigger clearing of the search input. */
  public clearSearchInput: boolean;

  /** @type {FormGroup} The main reactive form for business selection. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag controlling the visibility of the business selection dropdown. Default is `false`. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered list of options for the business autocomplete dropdown. Default is `[]`. */
  public filteredOptions: any[] = [];

  /** @type {string} Placeholder or value for the business search input. Default is 'Enter the business name'. */
  public businessSearch: string = 'Enter the business name';

  /** @type {boolean} Flag indicating if the form has been submitted. */
  public submitted: boolean;

  /** @type {boolean} Flag to disable the submit button during processing. */
  public submitButtondisabled: boolean;

  /**
   * Constructor for ProviderSummaryReportComponent.
   * Initializes the component and injects necessary services.
   *
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @param {ExcelService} excelService - Service for exporting data to Excel.
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {BusinessTypeService} businessTypeService - Service for business type operations.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {CustomerPondService} customerPondService - Service for customer pond operations.
   * @param {ReadingsService} readingsService - Service for readings operations (used for export).
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service ToastrService
   * @service ExcelService
   * @service ActivatedRoute
   * @service FormBuilder
   * @service BusinessTypeService
   * @service BusinessEntityService
   * @service CustomerPondService
   * @service ReadingsService
   * @service CommonMethodsService
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public excelService: ExcelService, public route: ActivatedRoute, public formBuilder: FormBuilder, public businessTypeService: BusinessTypeService, public businessEntityService: BusinessEntityService, public customerPondService: CustomerPondService, public readingsService: ReadingsService,
    public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.customerPond = new CustomerPondModel();
    // Initialize the register form with validation
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required]
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Sets up the previous route.
   * - Fetches all business entities and filters them to include only specific types (likely Feed Providers).
   * - If the logged-in user is a 'Business Admin' of a 'Provider', it automatically sets the business context.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    this.appPermission = this.route.snapshot.data['rolepermission'];
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // login person Business name
      this.keyword = 'businessName';
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.isSearchClicked = false;
      this.clearSearchInput = false;
      this.businessSearch = undefined;
      this.registerForm.get('businessfield').patchValue('');
      let feedProvidersBusiness = await this.businessEntityService.getAllBusiness();
      this.businessEntities = feedProvidersBusiness.filter(businessEntity => ['618a17b80f339c001e991b03', '632aeccb0d7d43001d8813c2'].includes(businessEntity.businessType && businessEntity.businessType.length > 0 && businessEntity.businessType[0].businessTypeID && businessEntity.businessType[0].businessTypeID
      ));
      this.filteredOptions = this.businessEntities;
      this.customerPond.feedProviderId = '';
      this.isSearchClicked = false; // if data is not available
      this.dupProviderSummaryList = new Array();
      if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        this.registerForm.get('businessfield').patchValue(this.loginUserBusinessName);
        this.customerPond.feedProviderId = localStorage.businessID;
        this.customerPond.businessName = this.loginBusinessName;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method filterBusinesses
   * @description Filters the `businessEntities` list based on the user's input in the business field.
   * Updates `filteredOptions` with the result.
   *
   * @param {KeyboardEvent} event - The keyboard event triggering the filter.
   * @side-effect Updates `filteredOptions`.
   */
  public filterBusinesses(event: KeyboardEvent): void {
    this.filteredOptions = [];
    const value = this.businessSearch && this.businessSearch.replace(/[-_]|\s/g, '').toLowerCase();
    if (value && (event.key != 'Backspace' || event.key == 'Backspace')) {
      this.filteredOptions = this.businessEntities.filter(obj => obj.businessName.replace(/[-_]|\s/g, '').toLowerCase().includes(value));
    } else if (!value) {
      this.filteredOptions = this.businessEntities;
    }
  }

  /**
   * @method selectOption
   * @description Handles the selection of a business entity from the dropdown.
   * Updates the search field and the `customerPond.feedProviderId` model.
   *
   * @param {BusinessEntity} business - The selected business entity.
   * @side-effect Updates `isDropdownOpen`, `businessSearch`, `customerPond.feedProviderId`.
   */
  public selectOption(business: BusinessEntity): void {
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.customerPond.feedProviderId = business.id;
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
  }

  /**
   * @method providerSummary
   * @async
   * @description Fetches and processes the provider summary report.
   * - Validates the form.
   * - Calls `customerPondService.getFeedProviderSumaryByFarmSite`.
   * - Flattens the response (Ponds -> Devices) into a linear list.
   * - Enriches device objects with business and pond information.
   * - Separates devices into `providerSummaryList` and `loginProviderSummaryList` based on ownership.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async providerSummary() {
    try {
      this.submitted = true;
      if (this.registerForm.invalid) {
        return;
      }
      this.responseLoader = true;
      this.isSearchClicked = false;
      this.submitButtondisabled = true;
      // this.customerPonds = await this.customerPondService.getFeedProviderSumaryByFarmSite(inputData);
      this.customerPonds = await this.customerPondService.getFeedProviderSumaryByFarmSite(this.customerPond.feedProviderId);
      this.submitButtondisabled = false;
      if (this.customerPonds !== undefined) {
        this.responseLoader = false;
        this.providerSummaryList = new Array(); // for display I am preparing my own array
        this.loginProviderSummaryList = new Array();  // for display req body id and response provider id is same
        if (this.customerPonds && this.customerPonds.length > 0) {
          this.isSearchClicked = true;
          for (let i = 0; i < this.customerPonds.length; i++) { // loop all objects
            if (this.customerPonds[i].businessName && this.customerPonds[i].customerDevices && this.customerPonds[i].customerDevices.length > 0) { // check business name is tehre and device list is there
              for (let j = 0; j < this.customerPonds[i].customerDevices.length; j++) {
                if (this.customerPonds[i].barcodeSequence.length > 0) {
                  this.customerPonds[i].customerDevices[j].barcode_sequence = this.customerPonds[i].barcodeSequence[0].barcode_sequence;
                } else {
                  this.customerPonds[i].customerDevices[j].barcode_sequence = '--';
                }
                this.customerPonds[i].customerDevices[j].businessName = this.customerPonds[i].businessName;
                this.customerPonds[i].customerDevices[j].customerPond = this.customerPonds[i].customerPond;
                if (this.customerPond.feedProviderId != this.customerPonds[i].customerDevices[j].business_id) {
                  this.providerSummaryList.push(this.customerPonds[i].customerDevices[j]);
                } else if (this.customerPond.feedProviderId == this.customerPonds[i].customerDevices[j].business_id) {
                  this.loginProviderSummaryList.push(this.customerPonds[i].customerDevices[j]);
                }
              }
            }
          }
        }
        this.dupProviderSummaryList = this.providerSummaryList;
        if (this.providerSummaryList && this.providerSummaryList.length <= 0) {
          this.toastrService.warning('Data not available', '', {
            timeOut: 1000
          });
          this.responseLoader = false;
          this.customerPond.feedProviderId = '';
          this.isSearchClicked = false;
          this.submitButtondisabled = false;
        }
      } else {
        this.responseLoader = true;
        this.submitButtondisabled = false;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.submitButtondisabled = false;
    }
  }

  /**
   * @method exportExcel
   * @async
   * @description Generates and downloads an Excel report of the provider summary.
   * Finds the business name for the selected ID and triggers the download via `readingsService`.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async exportExcel() {
    try {
      /*find business name by selected businessid*/
      const selectedBusinessObj = _.filter(this.businessEntities, (o) => {
        return o.id == this.customerPond.feedProviderId;
      });
      if (selectedBusinessObj.length > 0) {
        this.customerPond.businessName = selectedBusinessObj[0].businessName;
      }
      /*find business name by selected businessid*/
      let inputData = new ReadingsModel();
      inputData.feedProviderId = this.customerPond.feedProviderId;
      inputData.businessName = this.customerPond.businessName;
      const fileObj = this.readingsService.excelReportsDownload('providerSummary', inputData);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'Provider Summary Report');
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method searchProviderSummary
   * @description Filters the displayed `providerSummaryList` based on the user's search input.
   * Matches against `businessName` and `device.number`.
   *
   * @param {KeyboardEvent} event - The keyboard event triggering the search.
   * @side-effect Updates `providerSummaryList`, `NoDataMessage`, `clearSearchInput`.
   */
  public searchProviderSummary(event: KeyboardEvent) {
    this.currentPageNumber = 0;
    const value = this.search && this.search.replace(/[-_]|\s/g, '').toLowerCase();
    if (value && (event.key != 'Backspace' || event.key == 'Backspace')) {
      this.NoDataMessage = false;
      this.clearSearchInput = true;
      this.providerSummaryList = this.dupProviderSummaryList.filter(providerSummary => providerSummary && providerSummary.businessName.replace(/[-_.]|\s/g, '').toLowerCase().startsWith(value) ||
        providerSummary && providerSummary.device && providerSummary.device.number.toLowerCase().startsWith(value));
      this.NoDataMessage = this.providerSummaryList && this.providerSummaryList.length <= 0 ? true : false;
    } else if (!value) {
      this.currentPageNumber = this.activePageNo != this.currentPageNumber ? this.activePageNo : 0;
      this.providerSummaryList = this.dupProviderSummaryList;
      this.NoDataMessage = false;
      this.clearSearchInput = false;
    }
  }
}
